import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { RecruitmentJob, createJob, updateJob, publishJob } from '../../services/recruitmentService';

interface JobFormProps {
  job?: RecruitmentJob | null;
  onClose: () => void;
  onSuccess: () => void;
}

const JobForm: React.FC<JobFormProps> = ({ job, onClose, onSuccess }) => {
  const isEditing = !!job?.id;
  
  const [formData, setFormData] = useState<Partial<RecruitmentJob>>({
    title: job?.title || '',
    description: job?.description || '',
    responsibilities: job?.responsibilities || [],
    requirements: job?.requirements || [],
    nice_to_haves: job?.nice_to_haves || [],
    department: job?.department || '',
    location: job?.location || '',
    remote_type: job?.remote_type || 'onsite',
    employment_type: job?.employment_type || 'full-time',
    salary_min: job?.salary_min || undefined,
    salary_max: job?.salary_max || undefined,
    salary_currency: job?.salary_currency || 'GBP',
    benefits: job?.benefits || [],
    positions_available: job?.positions_available || 1,
    experience_level: job?.experience_level || 'mid',
    status: job?.status || 'draft',
    posted_to_career_site: job?.posted_to_career_site ?? true,
  });

  const [currentResponsibility, setCurrentResponsibility] = useState('');
  const [currentRequirement, setCurrentRequirement] = useState('');
  const [currentNiceToHave, setCurrentNiceToHave] = useState('');
  const [currentBenefit, setCurrentBenefit] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChange = (field: keyof RecruitmentJob, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addItem = (field: 'responsibilities' | 'requirements' | 'nice_to_haves' | 'benefits', value: string) => {
    if (!value.trim()) return;
    const currentItems = formData[field] || [];
    setFormData(prev => ({
      ...prev,
      [field]: [...currentItems, value.trim()]
    }));
    
    // Clear input
    if (field === 'responsibilities') setCurrentResponsibility('');
    if (field === 'requirements') setCurrentRequirement('');
    if (field === 'nice_to_haves') setCurrentNiceToHave('');
    if (field === 'benefits') setCurrentBenefit('');
  };

  const removeItem = (field: 'responsibilities' | 'requirements' | 'nice_to_haves' | 'benefits', index: number) => {
    const currentItems = formData[field] || [];
    setFormData(prev => ({
      ...prev,
      [field]: currentItems.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async (publishNow: boolean = false) => {
    try {
      setSaving(true);

      // Validation
      if (!formData.title?.trim()) {
        toast.error('Job title is required');
        return;
      }

      // Save job
      let savedJob;
      if (isEditing && job?.id) {
        savedJob = await updateJob(job.id, formData);
        toast.success('Job updated successfully');
      } else {
        savedJob = await createJob(formData as RecruitmentJob);
        toast.success('Job created successfully');
      }

      // Publish if requested
      if (publishNow && savedJob.id) {
        await publishJob(savedJob.id);
        toast.success('Job published!');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to save job:', error);
      toast.error(error.message || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Job' : 'Create New Job'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Fill in the details to post your job opening
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-8">
          {/* Basic Info */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g. Senior Software Engineer"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => handleChange('department', e.target.value)}
                    placeholder="e.g. Engineering"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="e.g. London, UK"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Remote Type</label>
                  <select
                    value={formData.remote_type}
                    onChange={(e) => handleChange('remote_type', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="onsite">On-site</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="remote">Remote</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
                  <select
                    value={formData.employment_type}
                    onChange={(e) => handleChange('employment_type', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
                  <select
                    value={formData.experience_level}
                    onChange={(e) => handleChange('experience_level', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Positions Available</label>
                <input
                  type="number"
                  min="1"
                  value={formData.positions_available}
                  onChange={(e) => handleChange('positions_available', parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </section>

          {/* Description */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Description</h3>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the role, team, and company culture..."
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </section>

          {/* Responsibilities */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Responsibilities</h3>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentResponsibility}
                  onChange={(e) => setCurrentResponsibility(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('responsibilities', currentResponsibility))}
                  placeholder="Add a responsibility..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => addItem('responsibilities', currentResponsibility)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Add
                </button>
              </div>
              <div className="space-y-2">
                {formData.responsibilities?.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                    <span className="flex-1 text-sm text-gray-700">• {item}</span>
                    <button
                      type="button"
                      onClick={() => removeItem('responsibilities', index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Requirements */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Requirements</h3>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentRequirement}
                  onChange={(e) => setCurrentRequirement(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('requirements', currentRequirement))}
                  placeholder="Add a requirement..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => addItem('requirements', currentRequirement)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Add
                </button>
              </div>
              <div className="space-y-2">
                {formData.requirements?.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                    <span className="flex-1 text-sm text-gray-700">• {item}</span>
                    <button
                      type="button"
                      onClick={() => removeItem('requirements', index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Nice to Haves */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Nice to Have</h3>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentNiceToHave}
                  onChange={(e) => setCurrentNiceToHave(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('nice_to_haves', currentNiceToHave))}
                  placeholder="Add a nice-to-have skill..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => addItem('nice_to_haves', currentNiceToHave)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Add
                </button>
              </div>
              <div className="space-y-2">
                {formData.nice_to_haves?.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                    <span className="flex-1 text-sm text-gray-700">• {item}</span>
                    <button
                      type="button"
                      onClick={() => removeItem('nice_to_haves', index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Compensation */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Compensation</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Min Salary</label>
                  <input
                    type="number"
                    value={formData.salary_min || ''}
                    onChange={(e) => handleChange('salary_min', e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="50000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Salary</label>
                  <input
                    type="number"
                    value={formData.salary_max || ''}
                    onChange={(e) => handleChange('salary_max', e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="70000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                  <select
                    value={formData.salary_currency}
                    onChange={(e) => handleChange('salary_currency', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Benefits</h3>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentBenefit}
                  onChange={(e) => setCurrentBenefit(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('benefits', currentBenefit))}
                  placeholder="e.g. Health insurance, Remote work..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => addItem('benefits', currentBenefit)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.benefits?.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full">
                    <span className="text-sm text-purple-700">{item}</span>
                    <button
                      type="button"
                      onClick={() => removeItem('benefits', index)}
                      className="text-purple-500 hover:text-purple-700"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Publishing Options */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Publishing</h3>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="career_site"
                checked={formData.posted_to_career_site}
                onChange={(e) => handleChange('posted_to_career_site', e.target.checked)}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label htmlFor="career_site" className="text-sm text-gray-700">
                Post to career site
              </label>
            </div>
          </section>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            disabled={saving}
          >
            Cancel
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleSave(false)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save as Draft'}
            </button>
            <button
              type="button"
              onClick={() => handleSave(true)}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-lg"
              disabled={saving}
            >
              {saving ? 'Publishing...' : isEditing ? 'Save & Publish' : 'Create & Publish'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobForm;
